import {
  GraphQLBoolean,
  GraphQLEnumType,
  GraphQLID,
  GraphQLInt,
  GraphQLList,
  GraphQLNonNull,
  GraphQLObjectType,
  GraphQLString,
  GraphQLInputObjectType
} from 'graphql'
import {
  ApplicationImportance,
  ApplicationImportanceType
} from './application-importance'
import { RuleSeverity, RuleSeverityType } from './rule-severity'
import { serverEnvironmentType } from './server'
import { BaseAssessRule, baseAssessRule } from './assess-rule'
import { BaseApplication, baseApplication } from './application'
import { AutoVerificationType } from '../../../components/vulnerability-management'

export type VulnerabilityPolicyAction = 'NOTIFY' | 'AUTO_REMEDIATE'

export const vulnerabilityPolicyAction = new GraphQLEnumType({
  name: 'VulnerabilityPolicyAction',
  description: 'Vulnerability Policy type',
  values: {
    NOTIFY: {
      value: 'NOTIFY'
    },
    AUTO_REMEDIATE: {
      value: 'AUTO_REMEDIATE'
    }
  }
})

export interface VulnerabilityPolicyApplication {
  readonly id: string
  readonly name: string
}

export const vulnerabilityPolicyApplication = new GraphQLObjectType({
  name: 'VulnerabilityPolicyApplication',
  fields: () => ({
    id: {
      description: 'Application ID',
      type: new GraphQLNonNull(GraphQLID)
    },
    name: {
      description: 'Application name',
      type: new GraphQLNonNull(GraphQLString)
    }
  })
})

export interface BaseVulnerabilityPolicy {
  readonly id: string
  readonly name: string
  readonly enabled: boolean
  readonly allRules: boolean
  readonly allApplications: boolean
  readonly serverEnvironments: string[]
  readonly allServerEnvironments: boolean
  readonly applicationImportance: ApplicationImportance[]
  readonly ruleSeverities: RuleSeverity[]
  readonly remediationDays: number
  readonly valid: boolean
  readonly action: VulnerabilityPolicyAction
  readonly timeBasedEnabled: boolean
  readonly routeBasedEnabled: boolean
  autoVerificationType: AutoVerificationType
}

export interface VulnerabilityPolicy extends BaseVulnerabilityPolicy {
  readonly rules: BaseAssessRule[]
  readonly applications: BaseApplication[]
}

export const vulnerabilityPolicyType = new GraphQLObjectType({
  name: 'VulnerabilityPolicy',
  fields: () => ({
    id: {
      description: 'The policy unique ID ',
      type: new GraphQLNonNull(GraphQLID)
    },
    name: {
      description: 'The policy name',
      type: new GraphQLNonNull(GraphQLString)
    },
    enabled: {
      description: 'Indicates if the policy is enabled',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    rules: {
      description: 'List of rules',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(baseAssessRule))
      )
    },
    allRules: {
      description: 'Apply to all rules',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    applications: {
      description: 'List of applications',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(baseApplication))
      )
    },
    allApplications: {
      description: 'Apply to all applications',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    serverEnvironments: {
      description: 'List of server environments',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(serverEnvironmentType))
      )
    },
    allServerEnvironments: {
      description: 'Apply to all server environments',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    applicationImportance: {
      description: 'List of application importance',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(ApplicationImportanceType))
      )
    },
    ruleSeverities: {
      description: 'List of rule severities',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(RuleSeverityType))
      )
    },
    remediationDays: {
      description: 'Remediation days',
      type: new GraphQLNonNull(GraphQLInt)
    },
    valid: {
      description: 'Is policy valid?',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    action: {
      description: 'Action taken by the policy on vulnerabilities',
      type: new GraphQLNonNull(vulnerabilityPolicyAction)
    },
    timeBasedEnabled: {
      description: 'Is time trigger enabled?',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    routeBasedEnabled: {
      description: 'Is route based trigger enabled?',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    autoVerificationType: {
      description: 'Auto-verification type ',
      type: new GraphQLEnumType({
        name: 'AutoVerificationType',
        description: 'Auto-Verification Type',
        values: {
          SESSION: { value: 'SESSION' },
          ROUTE: { value: 'ROUTE' },
          NONE: { value: 'NONE' }
        }
      })
    }
  })
})

export interface VulnerabilityPolicyInput extends BaseVulnerabilityPolicy {
  readonly rules: string[]
  readonly applications: string[]
}

export const vulnerabilityPolicyInput = new GraphQLInputObjectType({
  name: 'VulnerabilityPolicyInput',
  fields: () => ({
    name: {
      description: 'The policy name',
      type: new GraphQLNonNull(GraphQLString)
    },
    rules: {
      description: 'List of rules',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(GraphQLString))
      )
    },
    allRules: {
      description: 'Apply to all rules',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    applications: {
      description: 'List of applications',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(GraphQLString))
      )
    },
    allApplications: {
      description: 'Apply to all applications',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    serverEnvironments: {
      description: 'List of server environments',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(GraphQLString))
      )
    },
    allServerEnvironments: {
      description: 'Apply to all server environments',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    applicationImportance: {
      description: 'List of application importance',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(GraphQLString))
      )
    },
    ruleSeverities: {
      description: 'List of rule severities',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(GraphQLString))
      )
    },
    remediationDays: {
      description: 'Remediation days',
      type: new GraphQLNonNull(GraphQLInt)
    },
    routeBasedEnabled: {
      description: 'Is route-based configuration enabled?',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    action: {
      description: 'Action taken by the policy on vulnerabilities',
      type: new GraphQLNonNull(GraphQLString)
    },
    autoVerificationType: {
      description: '',
      type: new GraphQLEnumType({
        name: 'AutoVerification',
        description: 'Auto-Verification Type',
        values: {
          SESSION: { value: 'SESSION' },
          ROUTE: { value: 'ROUTE' },
          NONE: { value: 'NONE' }
        }
      })
    }
  })
})
